import { AlertCircle, Landmark, RefreshCw } from "lucide-react";
import { useState } from "react";

import { OrganizationScopePicker } from "@/modules/dashboard/payments-config/components/OrganizationScopePicker";
import { useOrganizationsCatalog } from "@/modules/dashboard/payments-config/hooks/useOrganizationsCatalog";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaIconButton } from "@/shared/components/buttons/ColocaIconButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaPageHeader } from "@/shared/components/layout/ColocaPageHeader";
import { NoAccessView } from "@/shared/components/NoAccessView";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import { NewWalletTopUpDialog } from "../components/WalletTopUpDialog";
import { WalletTopUpsList } from "../components/WalletTopUpsList";
import { useWalletTopUps } from "../hooks/useWalletTopUps";

export default function WalletTopUpsPage() {
  const { t } = useTranslations("walletTopUps");
  const { t: tBatches } = useTranslations("payoutBatches");
  const catalog = useOrganizationsCatalog();
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const history = useWalletTopUps(organizationId);

  if (!history.hasAccess) return <NoAccessView />;

  return (
    <div className="mx-auto w-full max-w-[1400px] space-y-6 p-2">
      <ColocaPageHeader
        title={t("page_title")}
        description={t("page_subtitle")}
        actions={
          <div className="flex items-center gap-2">
            <ColocaIconButton
              icon={RefreshCw}
              variant="ghost"
              size="sm"
              aria-label={t("action_refresh")}
              className={cn(history.isLoading && "[&_svg]:animate-spin")}
              onClick={history.reload}
            />
            <ColocaButton variant="primary" icon={Landmark} onClick={() => setCreating(true)}>
              {t("action_new")}
            </ColocaButton>
          </div>
        }
      />

      <OrganizationScopePicker
        id="wallet-top-ups-scope"
        className="max-w-md"
        value={organizationId}
        onValueChange={setOrganizationId}
        catalog={catalog}
        labels={{
          label: t("filter_organization"),
          placeholder: tBatches("org_picker_placeholder"),
          loading: tBatches("org_picker_loading"),
          empty: tBatches("org_picker_empty"),
          searching: tBatches("org_picker_searching"),
          searchError: tBatches("org_picker_search_error"),
          catalogError: tBatches("org_picker_catalog_error"),
          catalogTruncated: tBatches("org_picker_catalog_truncated"),
          globalOption: t("filter_all_option"),
          globalHint: t("filter_all_hint"),
          orgHint: t("filter_org_hint"),
        }}
      />

      {history.error ? (
        <ColocaAlert
          variant="destructive"
          icon={AlertCircle}
          text={history.error}
          actions={
            <ColocaButton variant="default" size="sm" icon={RefreshCw} onClick={history.reload}>
              {t("action_retry")}
            </ColocaButton>
          }
        />
      ) : (
        <WalletTopUpsList
          rows={history.rows}
          isLoading={history.isLoading}
          page={history.page}
          total={history.total}
          onPageChange={history.setPage}
        />
      )}

      <NewWalletTopUpDialog open={creating} onClose={() => setCreating(false)} onCreated={history.reload} />
    </div>
  );
}
